import type { Edge, Node } from "reactflow";
import { MarkerType, Position } from "reactflow";

import { nodeCategories, nodeType } from "../../config/drawingConstants";

// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
export function getNodeIntersection(intersectionNode: any, targetNode: any) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  const {
    width: intersectionNodeWidth,
    height: intersectionNodeHeight,
    positionAbsolute: intersectionNodePosition
  } = intersectionNode;
  const targetPosition = targetNode.positionAbsolute;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + w;
  const y1 = targetPosition.y + h;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(node: any, intersectionPoint: any) {
  const n = { ...node.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source: any, target: any) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos
  };
}

export function createNodesAndEdges() {
  const nodes = [];
  const edges = [];
  const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  nodes.push({ id: "target", data: { label: "Target" }, position: center });

  for (let i = 0; i < 8; i++) {
    const degrees = i * (360 / 8);
    const radians = degrees * (Math.PI / 180);
    const x = 250 * Math.cos(radians) + center.x;
    const y = 250 * Math.sin(radians) + center.y;

    nodes.push({ id: `${i}`, data: { label: "Source" }, position: { x, y } });

    edges.push({
      id: `edge-${i}`,
      target: "target",
      source: `${i}`,
      type: "special",
      markerEnd: {
        type: MarkerType.Arrow
      }
    });
  }

  return { nodes, edges };
}

// we have to make sure that parent nodes are rendered before their children
export const sortNodes = (a: Node, b: Node): number => {
  if (a.type === b.type) {
    return 0;
  }
  if (a.type !== nodeCategories.special && a.parentNode) {
    return 0;
  }
  return a.type !== nodeCategories.special ? -1 : 1;
};

export const getNodePositionInsideParent = (node: Partial<Node>, groupNode: Node) => {
  const position = node.position ?? { x: 0, y: 0 };
  const nodeWidth = node.width ?? 0;
  const nodeHeight = node.height ?? 0;
  const groupWidth = groupNode.width ?? 0;
  const groupHeight = groupNode.height ?? 0;

  if (position.x < groupNode.position.x) {
    position.x = 0;
  } else if (position.x + nodeWidth > groupNode.position.x + groupWidth) {
    position.x = groupWidth - nodeWidth;
  } else {
    position.x = position.x - groupNode.position.x;
  }

  if (position.y < groupNode.position.y) {
    position.y = 0;
  } else if (position.y + nodeHeight > groupNode.position.y + groupHeight) {
    position.y = groupHeight - nodeHeight;
  } else {
    position.y = position.y - groupNode.position.y;
  }
  return position;
};

//get cultivation data
const getCultivationData = (chartNodes: any, chartEdges: any, nodeItem: any) => {
  const getEdgesData = chartEdges.filter((e: any) => e.target === nodeItem.id);
  const getNodesItems = chartNodes.filter((e: any) => e.id === getEdgesData[0]?.source);
  return getNodesItems;
};

const farmGateInternalTransportModalValidation = (data: any, checkHaveEdges: any) => {
  const isDisconnected = !checkHaveEdges || !data.farmGateId;
  return {
    status: true,
    data: {
      farmGateId: data.farmGateId,
      nodeItem: data.nodeItem,
      isDisconnected
    },
    errorMessage: ""
  };
};

const connectionAwareModalData = (data: Record<string, any>, checkHaveEdges: boolean) => ({
  ...data,
  hasIncomingConnection: !!checkHaveEdges,
  requiresIncomingConnection: true
});

const farmGateStorageAndTransportDataModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.farmGateId;
  return {
    // Allow open without connection (fields disabled); keep prerequisite checks when connected
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        packagingId: data.packagingId,
        farmGateId: data.farmGateId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet
      ? `Please create cultivation before create ${
          data.nodeItem?.data.icon ? "internal transportation" : "internal storage"
        }`
      : ""
  };
};

const farmGatePackagingModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.farmGateId;
  return {
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        packagingId: data.packagingId,
        farmGateId: data.farmGateId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet ? "Please create cultivation before create packaging" : ""
  };
};

const farmGateProcessingModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.farmGateId;
  return {
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        ingredientId: data.ingredientId,
        farmGateId: data.farmGateId,
        supplierId: data.supplierId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet ? "Please create cultivation before create Processing" : ""
  };
};

const generalClientModalValidation = (data: any, checkHaveEdges: any) => {
  return {
    status: true,
    data: connectionAwareModalData(
      {
        clientId: data.clientId,
        sourceId: data.sourceId,
        ownerId: data.ownerId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: ""
  };
};

const processGateProcessingModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.processGateId;
  return {
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        ingredientId: data.ingredientId,
        processGateId: data.processGateId,
        processGateFacilityId: data.processGateFacilityId,
        supplierId: data.supplierId,
        nodeItem: data.nodeItem,
        ownerId: data.ownerId,
        sourceId: data.sourceId
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet ? "Please create Process Gate before create a process" : ""
  };
};

const processGateInternalTransportModalValidation = (data: any, checkHaveEdges: any) => {
  const isDisconnected = !checkHaveEdges || !data.processGateId;
  return {
    status: true,
    data: {
      processGateId: data.processGateId,
      nodeItem: data.nodeItem,
      isDisconnected
    },
    errorMessage: ""
  };
};

const processGateStorageAndTransportDataModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.processGateId;
  return {
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        processGateId: data.processGateId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet
      ? `Please create Process Gate before create ${
          data.nodeItem?.data.icon ? "internal transportation" : "internal storage"
        }`
      : ""
  };
};

const farmGateCultivationModalValidation = (
  data: any,
  checkHaveEdges: any,
  chartNodes: any,
  chartEdges: any,
  nodeItem: any,
  customFarmGateId: any
) => {
  // The Cultivar no longer requires an incoming Ingredient node: a farmer can
  // instead set the produce type directly on the Cultivar form. When an
  // Ingredient IS connected, we still pre-fill its id/supplierId so the old
  // flow (Ingredient -> Cultivar) keeps working exactly as before.
  const cultivationData = getCultivationData(chartNodes, chartEdges, nodeItem);
  const cultivationIngredientId = cultivationData?.[0]?.data?.reference?.ingredientId;
  const cultivationSupplierId = cultivationData?.[0]?.data?.reference?.supplierId;

  return {
    status: true,
    data: {
      ingredientId: cultivationIngredientId,
      farmGateId: customFarmGateId,
      supplierId: cultivationSupplierId,
      nodeItem: nodeItem,
      hasIncomingConnection: !!checkHaveEdges,
      requiresIncomingConnection: false
    },
    errorMessage: ""
  };
};

const processGatePackagingModalValidation = (data: any, checkHaveEdges: any) => {
  const prerequisitesMet = !!data.processGateId;
  return {
    status: !checkHaveEdges || prerequisitesMet,
    data: connectionAwareModalData(
      {
        packagingId: data.packagingId,
        processGateId: data.processGateId,
        nodeItem: data.nodeItem
      },
      checkHaveEdges
    ),
    errorMessage: !prerequisitesMet ? "Please create Farm Gate before create packaging" : ""
  };
};

const externalTransportModalValidation = (data: any, checkHaveEdges: any) => {
  const isDisconnected = !checkHaveEdges || !data.ownerId || !data.sourceId;
  return {
    status: true,
    data: {
      ownerId: data.ownerId,
      sourceId: data.sourceId,
      nodeItem: data.nodeItem,
      isDisconnected
    },
    errorMessage: ""
  };
};

const externalStorageModalValidation = (data: any, checkHaveEdges: any) => {
  const isDisconnected = !checkHaveEdges || !data.ownerId || !data.sourceId;
  return {
    status: true,
    data: {
      ownerId: data.ownerId,
      sourceId: data.sourceId,
      nodeItem: data.nodeItem,
      isDisconnected
    },
    errorMessage: ""
  };
};

//check node modal validation and return data
export const checkNodeModalValidation = (
  nodeItem: any,
  chartNodes: any,
  chartEdges: any,
  sourceId: any,
  ownerId: any
) => {
  const getParentId = nodeItem?.parentNode || null;
  const getNodeId = nodeItem?.id || null;
  const packagingId = nodeItem?.data?.reference?.packagingId || null;
  const getParentData = chartNodes.filter((e: any) => e.id === getParentId);
  const farmGateId = getParentData?.[0]?.data?.reference?.farmGateId || null;
  const ingredientId = getParentData?.[0]?.data?.reference?.ingredientId || null;
  const supplierId = getParentData?.[0]?.data?.reference?.supplierId || null;
  const customFarmGateId = nodeItem?.data?.reference?.farmGateId || null;
  const processGateId = getParentData?.[0]?.data?.reference?.processGateId || null;
  const processGateFacilityId = getParentData?.[0]?.data?.facilityId || null;
  const checkHaveEdges = chartEdges.some((e: any) => e.target === getNodeId);
  const externalTransportId = nodeItem?.data?.reference?.externalTransportId || null;
  const clientId = nodeItem?.data?.reference?.clientId || null;

  if (getParentData?.[0]?.data?.icon === nodeCategories.ingredientGate) {
    return {
      status: false,
      data: {},
      errorMessage: "Ingredient gate nodes can't update, it belongs to ingredient mapping!"
    };
  }

  let commonPassData = {
    packagingId: packagingId,
    farmGateId: farmGateId,
    nodeItem: nodeItem,
    supplierId: supplierId,
    ingredientId: ingredientId,
    processGateId: processGateId,
    processGateFacilityId: processGateFacilityId,
    sourceId: sourceId,
    ownerId: ownerId,
    externalTransportId: externalTransportId,
    clientId: clientId
  };

  switch (nodeItem?.data.icon) {
    case "2":
      return externalTransportModalValidation(commonPassData, checkHaveEdges);
    case "3":
      return externalStorageModalValidation(commonPassData, checkHaveEdges);
    case "7":
      return farmGateInternalTransportModalValidation(commonPassData, checkHaveEdges);
    case "9":
      return farmGateStorageAndTransportDataModalValidation(commonPassData, checkHaveEdges);
    case "8":
      return farmGatePackagingModalValidation(commonPassData, checkHaveEdges);
    case "6":
      return farmGateProcessingModalValidation(commonPassData, checkHaveEdges);
    case "5":
      return farmGateCultivationModalValidation(
        commonPassData,
        checkHaveEdges,
        chartNodes,
        chartEdges,
        nodeItem,
        customFarmGateId
      );
    case "4":
      return generalClientModalValidation(commonPassData, checkHaveEdges);
    case "11":
      return processGatePackagingModalValidation(commonPassData, checkHaveEdges);
    case "10":
      return processGateProcessingModalValidation(commonPassData, checkHaveEdges);
    case "12":
      return processGateInternalTransportModalValidation(commonPassData, checkHaveEdges);
    case "13":
      return processGateStorageAndTransportDataModalValidation(commonPassData, checkHaveEdges);
    default:
      return {
        status: true,
        data: {},
        errorMessage: ""
      };
  }
};

const cultivarConnectionValidation = (
  chartEdges: Edge[],
  chartNodes: Node[],
  nodeId: string,
  sourceNodeId: string
) => {
  let valid = true;
  let message = "";

  const sourceNode = chartNodes.find((node: any) => node.id === sourceNodeId);
  if (sourceNode?.data?.icon !== nodeType.Ingredients) {
    valid = false;
    message = "Cultivation can have incomings only from ingredients";
    return { valid, message };
  }
  const numOfCultivarConnection = chartEdges.filter((e: any) => e.target === nodeId).length;
  if (numOfCultivarConnection >= 1) {
    valid = false;
    message = "Cultivation can not have more than one incoming connection";
  }
  return {
    valid,
    message
  };
};

const targetNodeConnectionValidation = (
  chartEdges: Edge[],
  chartNodes: Node[],
  targetNode: Node,
  targetNodeId: string,
  sourceNodeId: string
) => {
  switch (targetNode?.data.icon) {
    case nodeType.Cultivar:
      return cultivarConnectionValidation(chartEdges, chartNodes, targetNodeId, sourceNodeId);
    default:
      return { valid: true, message: "" };
  }
};

const ingredientConnectionValidation = (chartNodes: Node[], targetNodeId: string) => {
  const targetNode = chartNodes.find((node: any) => node.id === targetNodeId);
  const targetNodeIcon = targetNode?.data?.icon;

  const allowedTargets = [
    nodeType.FarmGateProcess,
    nodeType.Cultivar,
    nodeType.ProcessGateProcessing,
    nodeType.Warehouse,
    nodeType.Transportation
  ];

  const disallowedNodeTypes: Record<string, string> = {
    [nodeType.FarmGatePacking]: "packaging",
    [nodeType.ProcessGatePacking]: "packaging",
    [nodeType.FarmGateInternalStorage]: "internal storage",
    [nodeType.ProcessGateInternalStorage]: "internal storage",
    [nodeType.FarmGateInternalTransport]: "internal transport",
    [nodeType.ProcessGateInternalTransport]: "internal transport"
  };

  if (targetNodeIcon && disallowedNodeTypes[targetNodeIcon]) {
    return {
      valid: false,
      message: `Ingredients are not allowed to connect to ${disallowedNodeTypes[targetNodeIcon]}`
    };
  }

  if (!allowedTargets.includes(targetNodeIcon)) {
    return {
      valid: false,
      message: "Ingredients can only connect to Process or Cultivation nodes."
    };
  }

  return { valid: true, message: "" };
};

const sourceNodeConnectionValidation = (
  chartNodes: Node[],
  sourceNode: Node,
  targetNodeId: string
) => {
  switch (sourceNode?.data.icon) {
    case nodeType.Ingredients:
      return ingredientConnectionValidation(chartNodes, targetNodeId);
    default:
      return { valid: true, message: "" };
  }
};

//check chart node edges validation
export const checkEdgeValidation = (
  connection: any,
  chartEdges: any,
  chartNodes: any,
  lockingStatus: boolean
) => {
  let isValid = true;
  const validationMessage: any[] = [];
  const sourceNodeId = connection.source;
  const targetNodeId = connection.target;
  const sourceNode = chartNodes.find((e: any) => e.id === sourceNodeId);
  const targetNode = chartNodes.find((e: any) => e.id === targetNodeId);

  if (lockingStatus) {
    validationMessage.push("Please create an edit request before adding a edge");
    return {
      isValid: false,
      validationMessage
    };
  }

  if (targetNode) {
    const targetNodeValidation = targetNodeConnectionValidation(
      chartEdges,
      chartNodes,
      targetNode,
      targetNodeId,
      sourceNodeId
    );
    isValid = targetNodeValidation.valid;
    validationMessage.push(targetNodeValidation.message);
    if (targetNode?.data.icon === nodeType.Ingredients) {
      isValid = chartEdges.some((e: any) => e.target === targetNodeId);
      // TODO:- This should be clarified from the business requirement
      validationMessage.push("Ingredient can only be a source item without a target connection");
    }
  }

  if (sourceNode && isValid) {
    const sourceNodeValidation = sourceNodeConnectionValidation(
      chartNodes,
      sourceNode,
      targetNodeId
    );
    isValid = sourceNodeValidation.valid;
    validationMessage.push(sourceNodeValidation.message);
  }

  return {
    isValid: isValid,
    validationMessage: validationMessage.filter(Boolean)
  };
};

const cultivarValidation = (node: Node, group: Node | undefined, sameNode: Node[]) => {
  let valid = true;
  let message = "";
  if (!group) {
    valid = false;
    message = "Cultivation can only be use inside a Farm gate";
  }
  if (node.parentNode && node.parentNode === group?.id && group?.type !== nodeCategories.farmGate) {
    valid = false;
    message = "Cultivar node must be use in farmGate group can not use in other groups!";
  }
  if (sameNode.some(sn => sn.id !== node.id) && group?.type === nodeCategories.farmGate) {
    valid = false;
    message = "One Farm Gate can have only one cultivation";
  }
  return {
    valid: valid,
    message: message
  };
};

const ingredientValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group && group?.type !== nodeCategories.ingredientGate) {
    valid = false;
    message = "Ingredient items can not use inside any groups!";
  }
  return {
    valid: valid,
    message: message
  };
};

const externalTransportValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group) {
    valid = false;
    message = "External Transport items can not use inside any groups!";
  }
  return {
    valid: valid,
    message: message
  };
};

const warehouseValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group) {
    valid = false;
    message = "External Warehouse items can not use inside any groups!";
  }
  return {
    valid: valid,
    message: message
  };
};

const clientValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group) {
    valid = false;
    message = "Client items can not use inside any groups!";
  }
  return {
    valid: valid,
    message: message
  };
};

const farmGateInternalTransportValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.farmGate) {
    valid = false;
    message = "Farm gate Internal Transport must only use in Farm gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const farmGateInternalStorageValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.farmGate) {
    valid = false;
    message = "Farm gate Internal Storage must only use in Farm gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const farmGateProcessValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.farmGate) {
    valid = false;
    message = "Farm gate Process must only use in Farm gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const farmGatePackingValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.farmGate) {
    valid = false;
    message = "Farm gate packaging must only use in Farm gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const processGateInternalTransportValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.processGate) {
    valid = false;
    message = "Process Gate Internal Transport must only use in Process Gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const processGateInternalStorageValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.processGate) {
    valid = false;
    message = "Process Gate Internal Storage must only use in Process Gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const processGatePackingValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.processGate) {
    valid = false;
    message = "Process Gate Packing must only use in Process Gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const processGateProcessingValidation = (node: Node, group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type !== nodeCategories.processGate) {
    valid = false;
    message = "Process Gate Processing must only use in Process Gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const farmGateValidation = (group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type === nodeCategories.processGate) {
    valid = false;
    message = "Farm Gate can not use in Process Gate group!";
  }
  if (group?.type === nodeCategories.farmGate) {
    valid = false;
    message = "Farm Gate can not use in another farm Gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

const processGateValidation = (group: Node | undefined) => {
  let valid = true;
  let message = "";
  if (group?.type === nodeCategories.farmGate) {
    valid = false;
    message = "Process Gate can not use in farm gate group!";
  }
  if (group?.type === nodeCategories.processGate) {
    valid = false;
    message = "Farm Gate can not use in another process gate group!";
  }
  return {
    valid: valid,
    message: message
  };
};

export const nodeValidationChecker = (
  node: Node,
  group: Node | undefined,
  chartNodes: Node[],
  lockingStatus: boolean
) => {
  let isValid = true;
  let validationMessage = "";

  if (lockingStatus) {
    validationMessage = "Please create an edit request before adding a node.";
    return {
      isValid: false,
      validationMessage
    };
  }

  if (group && group?.type === nodeCategories.ingredientGate) {
    return {
      isValid: isValid,
      validationMessage: validationMessage
    };
  }

  if (node.type === nodeCategories.farmGate) {
    const result = farmGateValidation(group);
    isValid = result.valid;
    validationMessage = result.message;
  }

  if (node.type === nodeCategories.processGate) {
    const result = processGateValidation(group);
    isValid = result.valid;
    validationMessage = result.message;
  }

  switch (node?.data.icon) {
    case nodeType.Ingredients:
      const ingredientResult = ingredientValidation(node, group);
      isValid = ingredientResult.valid;
      validationMessage = ingredientResult.message;
      break;
    case nodeType.FarmGateInternalTransport:
      const fgITransportResult = farmGateInternalTransportValidation(node, group);
      isValid = fgITransportResult.valid;
      validationMessage = fgITransportResult.message;
      break;
    case nodeType.Transportation:
      const externalTransportResult = externalTransportValidation(node, group);
      isValid = externalTransportResult.valid;
      validationMessage = externalTransportResult.message;
      break;
    case nodeType.FarmGateInternalStorage:
      const fgIStorageResult = farmGateInternalStorageValidation(node, group);
      isValid = fgIStorageResult.valid;
      validationMessage = fgIStorageResult.message;
      break;
    case nodeType.FarmGateProcess:
      const fgProcessResult = farmGateProcessValidation(node, group);
      isValid = fgProcessResult.valid;
      validationMessage = fgProcessResult.message;
      break;
    case nodeType.FarmGatePacking:
      const fgPackingResult = farmGatePackingValidation(node, group);
      isValid = fgPackingResult.valid;
      validationMessage = fgPackingResult.message;
      break;
    case nodeType.Client:
      const clientResult = clientValidation(node, group);
      isValid = clientResult.valid;
      validationMessage = clientResult.message;
      break;
    case nodeType.Warehouse:
      const warehouseResult = warehouseValidation(node, group);
      isValid = warehouseResult.valid;
      validationMessage = warehouseResult.message;
      break;
    case nodeType.Cultivar:
      const sameNodes = chartNodes.filter(
        cn => cn.parentNode && cn.parentNode === group?.id && cn?.data.icon === nodeType.Cultivar
      );
      const cultivarValidationResult = cultivarValidation(node, group, sameNodes);
      isValid = cultivarValidationResult.valid;
      validationMessage = cultivarValidationResult.message;
      break;
    case nodeType.ProcessGateInternalTransport:
      const pgITransportResult = processGateInternalTransportValidation(node, group);
      isValid = pgITransportResult.valid;
      validationMessage = pgITransportResult.message;
      break;
    case nodeType.ProcessGateInternalStorage:
      const pgIStorageResult = processGateInternalStorageValidation(node, group);
      isValid = pgIStorageResult.valid;
      validationMessage = pgIStorageResult.message;
      break;
    case nodeType.ProcessGatePacking:
      const pgPackingResult = processGatePackingValidation(node, group);
      isValid = pgPackingResult.valid;
      validationMessage = pgPackingResult.message;
      break;
    case nodeType.ProcessGateProcessing:
      const pgProcessingResult = processGateProcessingValidation(node, group);
      isValid = pgProcessingResult.valid;
      validationMessage = pgProcessingResult.message;
      break;
  }
  return {
    isValid: isValid,
    validationMessage: validationMessage
  };
};

export function stringToColor(str: string) {
  let colour = "#";
  let hash = 0;

  for (const char of str) {
    hash = char.charCodeAt(0) + (hash << 5) - hash;
  }

  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += value.toString(16).substring(-2);
  }

  return colour.substring(0, 7);
}
interface ChartNode {
  id: string;
  type: string;
  data: any;
  position: any;
  width: number;
  height: number;
}

// Check is node label is already exists
export const isNodeLabelExists = (chartNodes: ChartNode[], label: string) => {
  if (!label?.trim()) return false;

  return chartNodes.some(node => {
    let nodeLabel = node.data.label?.trim().toLowerCase();
    let checkLabel = label.trim().toLowerCase();

    // Extract name from parentheses if it exists in node label
    const nodeLabelMatch = nodeLabel.match(/\(([^)]+)\)/);
    if (nodeLabelMatch) {
      nodeLabel = nodeLabelMatch[1];
    }

    return nodeLabel === checkLabel;
  });
};
